import { GridColDef } from "@mui/x-data-grid";
import { useState, useEffect } from "react";
import api from "../../services/ApiService";
import { ApiResponse } from "../../models/ApiResponse";
import { User, UserRole } from "../../models/User";
import { Chip, Stack, Button, Switch } from "@mui/material";
import { GridRowParams, GridActionsCellItem } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import UserModal from "./UserModal";
import { toast } from "react-toastify";
import UserListing from "./UserListing";
import Loader from "../../components/Loader";
import { useFormik } from "formik";
import * as Yup from "yup";
import GridCellChipsContent from "../../components/GridCellChipsContent";
import SignalRConnector from "../../services/SignalRConnector";
import { Permissions } from "../../models/Enum";
import SessionService from "../../services/SessionService";

const UserList = () => {
  const [isLoading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [userEditOpen, setUserEditOpen] = useState(false);
  const [userListData, setUserListData] = useState([] as any);
  const [userEditId, setUserEditId] = useState(null);
  //const [updateRowsData, setUpdatedRowsData] = useState<boolean>(false);
  const [newRoleStatus, setNewRoleStatus] = useState(true);
  const [expandedRowIds, setExpandedRowIds] = useState<number[]>([]);
  const sessionService = SessionService.getInstance();

  const usersGridschema = [
    { field: "id", headerName: "ID", disableColumnMenu: true, width: 50 },
    {
      field: "name",
      headerName: "Name",
      width: 150,
      disableColumnMenu: true,
    },
    {
      field: "email",
      headerName: "Email",
      type: "string",
      width: 300,
      disableColumnMenu: true,
    },
    {
      field: "roles",
      headerName: "Role",
      type: "string",
      width: 400,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params: any) => {
        if (!params.value) {
          return params.value;
        } else {          
          return <GridCellChipsContent 
            keyField="id" 
            displayTextFiled="name" 
            cellData={params.row?.roles?.length > 3 && !expandedRowIds?.includes(params.row?.id) ? params.row?.roles.filter((_, i) => i < 3) : params.row?.roles}
            showViewMore={params.row?.roles?.length > 3}
            viewMoreText={expandedRowIds?.includes(params.row?.id) ? "Show Less" : "Show More"}
            viewMoreHandle={() => setExpandedRowIds(expandedRowIds?.includes(params.row?.id) ? expandedRowIds.filter(itemId => itemId !== params.row?.id) : [...expandedRowIds, params.row?.id])}
          />
        }
      },
    },
    {
      field: "brandCollections",
      headerName: "Brand Collection",
      type: "string",
      width: 300,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params: any) => {
        if (!params.value?.length) {
          return null;
        }
        return (
          <GridCellChipsContent
            keyField="id"
            displayTextFiled="description"
            cellData={
              params.row?.brandCollections?.length > 3 &&
              !expandedRowIds?.includes(params.row?.id)
                ? params.row?.brandCollections.filter((_, i) => i < 3)
                : params.row?.brandCollections
            }
            showViewMore={params.row?.brandCollections?.length > 3}
            viewMoreText={
              expandedRowIds?.includes(params.row?.id) ? "Show Less" : "Show More"
            }
            viewMoreHandle={() =>
              setExpandedRowIds(
                expandedRowIds?.includes(params.row?.id)
                  ? expandedRowIds.filter((itemId) => itemId !== params.row?.id)
                  : [...expandedRowIds, params.row?.id]
              )
            }
          />
        );
      },
    },
   {
      field: "isActive",
      headerName: "Status",
      disableColumnMenu: true,
      renderCell: (params: any) => (
        <>
          <Switch
            checked={params.value}
            onChange={(e) => {
              params.api.setEditCellValue(
                { id: params.id, field: params.field },
                e.target.checked
              );
              updateUsersStatus(params.row);
            }}
            color="primary"
            inputProps={{ "aria-label": "controlled" }}
            disabled= {!sessionService.hasPermission(Permissions.UpdateUser)}
          />
        </>
      ),
    },
    {
      field: "action",
      headerName: "Action",
      type: "actions",
      sortable: false,
      disableColumnMenu: true,
      getActions: (params: GridRowParams) => [
        <GridActionsCellItem
          icon={<EditIcon />}
          onClick={() => handleEditUser(params.id)}
          label="Edit"
        />,
      ],
    },
  ];

  useEffect(() => {
    getUserList();
  }, []);

  const updateUsersStatus = (user: User) => {
    const data = {
      id: user.id,
      name: user.name,
      email: user.email,
      isActive: !user.isActive,
      availableForAssignments: !!user.availableForAssignments,
      canBypassSkuValidation: !!user.canBypassSkuValidation,
      brandCollections: user.brandCollections?.map((bc) => bc.id) ?? [],
    };
    try {
      api
        .put<ApiResponse<User>>("/User", data)
        .then(async (response: ApiResponse<User>) => {
          if (response.isSuccess) {
            //setUpdatedRowsData(!updateRowsData);
            toast.success(response.message);
            await getUserList();
          } else {
            throw new Error(response.message);
          }
        })
        .catch((error) => {
          console.error("Exception from User", error);
        });
    } catch (err: any) {
      toast.error(err.message);
    }
  };
  const getUserList = async () => {
    try {
      api
        .get<ApiResponse<User>>(`/UserRole`)
        .then((response: ApiResponse<User>) => {
          if (response.isSuccess) {
            setLoading(false);
            setUserListData(response.data);
          } else {
            throw new Error(response.message);
          }
        })
        .catch((error) => {
          console.error("Exception from UserRole", error);
        });
    } catch (error: any) {
      console.error(error.message);
    }
  };

  const openAddModal = () => {
    setIsModalOpen(true);
  };

  const testSignalR = () => { 
    SignalRConnector().sendMessage("sendItemCreated", 1);
  }

  const closeModal = () => {
    setIsModalOpen(false);
    setUserEditOpen(false);
    formik.resetForm();
  };

  const handleRadioChange = () => {
    setNewRoleStatus(!newRoleStatus);
  };

  const handleEditUser = (editId: any) => {
    setUserEditId(editId);
    api
      .get<ApiResponse<User>>(`/UserRole/${editId}`)
      .then((response: ApiResponse<User>) => {
        if (response.isSuccess) {
          setIsModalOpen(true);

          const roleIds: string[] = response?.data?.roles?.map((role) =>
            role.id.toString()
          );

          formik.setValues({
            name: response.data.name,
            email: response.data.email,
            roles: roleIds,
            newRoleStatus: response.data.isActive,
            availableForAssignments: !!response.data.availableForAssignments,
            canBypassSkuValidation: !!response.data.canBypassSkuValidation,
            brandCollections:
              response.data.brandCollections?.map((bc) => bc.id) ?? [],
          });
          setUserEditOpen(true);
        } else {
          throw new Error(response.message);
        }
      })
      .catch((error) => {
        console.error("Exception from fetching userData for edit", error);
      });
  };

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      roles: [] as string[],
      newRoleStatus: true,
      availableForAssignments: false,
      canBypassSkuValidation: false,
      brandCollections: [] as number[],
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .trim()
        .email("Invalid email address")
        .required("Email is required"),
      name: Yup.string()
        .trim()
        .required("Name is required")
        .min(1, "Name must be at least 1 characters")
        .max(40, "Name must be at most 40 characters"),
      newRoleStatus: Yup.string().required("Select the status"),
      availableForAssignments: Yup.boolean(),
      canBypassSkuValidation: Yup.boolean(),
      brandCollections: Yup.array().when("availableForAssignments", {
        is: true,
        then: (schema) =>
          schema
            .min(1, "Brand Collection is required")
            .required("Brand Collection is required"),
        otherwise: (schema) => schema,
      }),
    }),
    onSubmit: (formData) => {
      const performUserApiCall = async (
        url: string,
        data: any,
        successMessage: string,
        isPost: boolean
      ) => {
        try {
          const response = await api[isPost ? "post" : "put"]<
            ApiResponse<User>
          >(url, data);

          if (response.isSuccess) {
            const userRoleData = {
              userId: isPost ? response.data : userEditId,
              roles: isPost ? formData?.roles : formData?.roles,
            };

            await api.post<ApiResponse<UserRole>>("/UserRole", userRoleData);

            toast.success(successMessage);
            setIsModalOpen(false);
            //setUpdatedRowsData(!updateRowsData);
            await getUserList();
          } else {
            throw new Error(response.message);
          }
        } catch (error: any) {
          console.error("Exception from login", error);
        }
      };

      if (userEditOpen) {
        const userUpdatedData = {
          id: userEditId,
          name: formData?.name.trim(),
          email: formData?.email.trim(),
          isActive: formData?.newRoleStatus,
          availableForAssignments: !!formData?.availableForAssignments,
          canBypassSkuValidation: !!formData?.canBypassSkuValidation,
          brandCollections: formData?.brandCollections ?? [],
        };

        formData.roles.length
          ? performUserApiCall(
              "/User",
              userUpdatedData,
              "User updated successfully",
              false
            )
          : toast.error("Role needs to be selected! ");
      } else {
        const userData = {
          name: formData?.name.trim(),
          email: formData?.email.trim(),
          isActive: formData?.newRoleStatus,
          availableForAssignments: !!formData?.availableForAssignments,
          canBypassSkuValidation: !!formData?.canBypassSkuValidation,
          brandCollections: formData?.brandCollections ?? [],
        };

        formData.roles.length
          ? performUserApiCall(
              "/User",
              userData,
              "User added successfully",
              true
            )
          : toast.error("Role needs to be selected! ");
      }
    },
  });

  return (
    <>
      {isLoading ? (
        <Loader isLoading={isLoading} />
      ) : (
        <>
          <div>
            {sessionService.hasPermission(Permissions.CreateUser) 
            && <Button onClick={openAddModal} variant="contained"
              sx = {{marginBottom: '.5%'}} 
            >
              Add User
            </Button>}
            {/* <Button onClick={testSignalR} variant="contained">
              SendMessage
            </Button> */}
            <UserModal
              closeModal={closeModal}
              isModalOpen={isModalOpen}
              userEditOpen={userEditOpen}
              handleRadioChange={handleRadioChange}
              formik={formik}
            />
          </div>
          <UserListing
            userListData={userListData}
            usersGridschema={usersGridschema}
          />
        </>
      )}
    </>
  );
};
export default UserList;
